import { useAssortment } from '../useAssortment'
import { useEffect, useMemo, useState } from 'react'
import { type Period, type SkuListGran } from '../landing'
import {
  AREA_COLOR, AREA_LABEL, AREA_LEAVES, AREA_ORDER, AREA_SEGMENTS, AREA_UNIT,
  segmentLabel, type Area, type Segment,
} from '../segments'
import { SkuList } from './SkuList'
import { formatDayShort, formatWeek } from '../format'
import { InfoTip } from './InfoTip'
import { SearchableSelect } from './SearchableSelect'

// MP-OS-103 / meeting 25/8 — the top lists as PER-AREA blocks (Snus / Vape / Smoking /
// Övrigt), each with its own article + brand leaderboards and unit (CU vs units). Which
// areas and leaf choices come from the supplier assortment, independent of period sales.
//
// ONE time control drives every block (Olof: the month-picker-up-top + week-toggle-in-the-list
// was confusing). Month follows the global period; Week lets you pick a SPECIFIC ISO week
// ("vecka 33 — 10 till 16"); Year is the rolling 12 months.

// The block-header pickers use the shared control style (.control-select), pushed right.
const pickerStyle = { marginLeft: 'auto' } as const

const isoDay = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

// The last `n` ISO weeks containing or preceding `endISO` (the latest can be partial).
// value = the Monday date
// (any date in the week works as the backend anchor); label = "Week 33 · 10 Aug–16 Aug" (format.ts formatWeek).
// MP-OS-188 bug #5 — a week whose Sunday falls after `dataEnd` (the actual data end, same
// value as `endISO` for every real caller) is still reported through `dataEnd` only, so its
// label and its `partial` flag must say that up front rather than reading like any other,
// fully-reported week.
export function recentWeeks(
  endISO: string,
  n = 16,
  historyFrom?: string,
  dataEnd: string = endISO,
): { value: string; label: string; partial: boolean }[] {
  const end = new Date(`${endISO}T00:00:00`)
  const monday = new Date(end)
  monday.setDate(end.getDate() - ((end.getDay() + 6) % 7))
  const out: { value: string; label: string; partial: boolean }[] = []
  for (let i = 0; i < n; i++) {
    const mon = new Date(monday)
    mon.setDate(monday.getDate() - i * 7)
    const sun = new Date(mon)
    sun.setDate(mon.getDate() + 6)
    if (historyFrom && isoDay(sun) < historyFrom) break
    const partial = isoDay(sun) > dataEnd
    const base = formatWeek(isoDay(mon))
    const label = partial ? `${base} · partial, through ${formatDayShort(dataEnd)}` : base
    out.push({ value: isoDay(mon), label, partial })
  }
  return out
}

// MP-OS-188 bug #3 — `recentWeeks` deliberately lists a trailing partial week (its Sunday
// hasn't happened/reported yet) so a user CAN pick it manually, but the DEFAULT selection must
// never land there — only on the latest week that is fully complete as of the actual data end.
// Handles the Sunday/Monday boundary, month/year rollover and ISO week 52/53/1 the same way
// `recentWeeks` does (plain calendar-day arithmetic via Date), and never assumes a partial week
// is the current/last one — it checks each option's own Sunday against `dataEnd`.
export function defaultWeek(weeks: { value: string }[], dataEnd: string): string {
  for (const w of weeks) {
    const sun = new Date(`${w.value}T00:00:00`)
    sun.setDate(sun.getDate() + 6)
    if (isoDay(sun) <= dataEnd) return w.value
  }
  return weeks[0]?.value ?? ''
}

export function AreaTopLists({ period, historyFrom, dataEnd }: { period: Period; historyFrom?: string; dataEnd?: string }) {
  const assortment = useAssortment()
  const areaSegments = (area: Area) => AREA_SEGMENTS[area].filter(s => assortment.segments.includes(s))
  const areaLeaves = (area: Area) => AREA_LEAVES[area].filter(l => assortment.data?.leaves.includes(l.value))
  const areas = assortment.data ? AREA_ORDER.filter(area => {
    if (area === 'smoking') return assortment.data!.segments.includes('Smoking')
    if (area === 'other') return assortment.data!.segments.includes('Other')
    return areaSegments(area).length > 0
  }) : null
  const selectedSegment = (area: Area) => {
    const options = areaSegments(area)
    return options.includes(segByArea[area] as Exclude<Segment, 'ALL'>) ? segByArea[area]! : options.length === 1 ? options[0] : 'ALL'
  }
  const selectedLeaf = (area: Area) => {
    const options = areaLeaves(area)
    return options.some(l => l.value === leafByArea[area]) ? leafByArea[area]! : ''
  }
  const [gran, setGran] = useState<SkuListGran>('month')
  // MP-OS-188 bug #3 — `period` is the ONE globally shared month (A4/C15), which by design
  // stays on the latest fully-reported MONTH (defaultPeriod skips an ongoing month) or on
  // whatever month the customer last looked at (it survives navigation and reload). Neither is
  // "the latest week with data": month settling lags behind week-level data by design, and a
  // remembered month can be older still. Anchor the Week picker on the actual data end (the
  // reporting periods' own latest `through`, same source _trend_data_end bounds) — never off
  // the selected month — so opening Week never lands on a stale week like 40 when 42 is
  // already complete.
  const weekAnchor = dataEnd ?? period.through ?? period.to
  const weeks = useMemo(() => recentWeeks(weekAnchor, 16, historyFrom), [weekAnchor, historyFrom])
  const [week, setWeek] = useState<string>('')
  // ONE category picker per block (MP-OS-135, Olof 18/9: "valet av kategori var lite konstigt
  // under en av stackarna"). Each block used to carry two independent pickers — one per list —
  // so choosing Nicotine-free in the articles list left the brands list on "All segments".
  // The block owns the choice now and both lists follow it. Snus blocks pick a segment; vape
  // and smoking blocks pick a sub-category leaf; a block with one option has no picker.
  const [segByArea, setSegByArea] = useState<Partial<Record<Area, Segment>>>({})
  const [leafByArea, setLeafByArea] = useState<Partial<Record<Area, string>>>({})
  // Default the week selector to the latest FULLY COMPLETED week whenever the option list or
  // data end changes — never the trailing partial week recentWeeks lists for manual picking.
  useEffect(() => {
    setWeek(defaultWeek(weeks, weekAnchor))
  }, [weeks, weekAnchor])

  const control = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '2px 0 16px', flexWrap: 'wrap' }}>
      <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--grey-text)' }}>Period</span>
      <span style={{ display: 'inline-flex', gap: 2, background: 'var(--brand-tint, #eef1f0)', padding: 3, borderRadius: 10 }}>
        {(['week', 'month', 'year'] as SkuListGran[]).map((g) => (
          <button
            key={g}
            aria-pressed={g === gran}
            onClick={() => setGran(g)}
            style={{
              border: 'none', background: g === gran ? 'var(--surface, #fff)' : 'transparent',
              color: g === gran ? 'var(--brand-dark)' : 'var(--ink, #333)', fontSize: 13, fontWeight: 600,
              padding: '6px 12px', borderRadius: 8, cursor: 'pointer',
              boxShadow: g === gran ? '0 1px 3px rgba(0,0,0,0.12)' : undefined,
            }}
          >
            {g === 'week' ? 'Week' : g === 'month' ? 'Month' : 'Last 12 months'}
          </button>
        ))}
      </span>
      {gran === 'week' && (
        <>
          <SearchableSelect
            label="Pick a week"
            searchLabel="Search weeks"
            showAllLabel="Show all weeks"
            value={week}
            onChange={setWeek}
            options={weeks}
          />
          {/* MP-OS-188 bug #5 — restates partial status next to the picker, not only inside the
              option text, so it is visible whether the customer picked the trailing week
              themselves or defaultWeek's fallback landed on it because nothing else qualified. */}
          {weeks.find((w) => w.value === week)?.partial && (
            <span style={{ fontSize: 12.5, color: 'var(--grey-text)' }}>
              Partial week — through {formatDayShort(weekAnchor)}
              <InfoTip label="About the partial week" size="small">This week is not finished, so it is not comparable with a full week.</InfoTip>
            </span>
          )}
        </>
      )}
      {gran === 'month' && (
        <span style={{ fontSize: 12.5, color: 'var(--grey-text)' }}>{period.label}</span>
      )}
    </div>
  )

  if (assortment.error) return <>{control}<div className="catalog-empty" role="alert">Could not load top-list categories. <button onClick={assortment.retry}>Try again</button></div></>
  if (!areas) return <>{control}<div className="catalog-empty">Loading…</div></>
  if (!areas.length) return <>{control}<div className="catalog-empty">No top lists for this supplier’s assortment.</div></>

  return (
    <>
      {control}
      {areas.map((area) => (
        <section key={area} className="area-block" style={{ borderLeft: `3px solid ${AREA_COLOR[area]}` }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, margin: '4px 0 10px' }}>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: AREA_COLOR[area], margin: 0 }}>
              {AREA_LABEL[area]}
            </h2>
            <span
              style={{
                fontSize: 11, color: '#fff', background: AREA_COLOR[area],
                padding: '2px 8px', borderRadius: 999,
              }}
            >
              {AREA_UNIT[area]}
            </span>
            {areaSegments(area).length > 1 && (
              <select
                value={selectedSegment(area)}
                onChange={(e) => setSegByArea((cur) => ({ ...cur, [area]: e.target.value as Segment }))}
                aria-label={`Filter ${AREA_LABEL[area]} by segment`}
                className="control-select" style={pickerStyle}
              >
                <option value="ALL">All segments</option>
                {areaSegments(area).map((sg) => (
                  <option key={sg} value={sg}>{segmentLabel(sg)}</option>
                ))}
              </select>
            )}
            {areaLeaves(area).length > 1 && (
              <select
                value={selectedLeaf(area)}
                onChange={(e) => setLeafByArea((cur) => ({ ...cur, [area]: e.target.value }))}
                aria-label={`Filter ${AREA_LABEL[area]} by sub-category`}
                className="control-select" style={pickerStyle}
              >
                <option value="">All sub-categories</option>
                {areaLeaves(area).map((l) => (
                  <option key={l.value} value={l.value}>{l.label}</option>
                ))}
              </select>
            )}
          </div>
          <SkuList
            month={period.value} area={area} gran={gran} week={gran === 'week' ? week : undefined} historyFrom={historyFrom} dataThrough={period.through ?? period.to}
            segment={selectedSegment(area)} onSegmentChange={(sg) => setSegByArea((cur) => ({ ...cur, [area]: sg }))}
            leaf={selectedLeaf(area)} onLeafChange={(l) => setLeafByArea((cur) => ({ ...cur, [area]: l }))}
          />
          <SkuList
            month={period.value} area={area} by="brand" gran={gran} week={gran === 'week' ? week : undefined} historyFrom={historyFrom} dataThrough={period.through ?? period.to}
            segment={selectedSegment(area)} onSegmentChange={(sg) => setSegByArea((cur) => ({ ...cur, [area]: sg }))}
            leaf={selectedLeaf(area)} onLeafChange={(l) => setLeafByArea((cur) => ({ ...cur, [area]: l }))}
          />
        </section>
      ))}
    </>
  )
}
